import { Split } from 'lucide-react'
import type { SplitResolution } from '@axilon-apps/domains-ontology-ui-state'
import { cn } from '../../lib/utils'
import type { ConflictSide } from './conflict-view-model'

export interface SplitDraft {
  keep: ConflictSide
  newId: string
  error?: string
}

/**
 * The per-conflict "split into two nodes" affordance for an add/add collision —
 * both branches newly added a node under the same id. Keep one side at the
 * original id and re-home the other as a new node under a new id. Shown only
 * for add/add node conflicts; advisory like every other pick (the merge does
 * the creation).
 */
export function ConflictSplitControl({
  targetName,
  sourceName,
  current,
  draft,
  interactive,
  onOpen,
  onDraftChange,
  onConfirm,
  onCancel
}: {
  targetName: string
  sourceName: string
  /** The active split selection, when this conflict is resolved by a split. */
  current?: SplitResolution
  /** The in-progress edit, or null when not editing. */
  draft: SplitDraft | null
  interactive: boolean
  onOpen: () => void
  onDraftChange: (draft: SplitDraft) => void
  onConfirm: () => void
  onCancel: () => void
}) {
  const keepName = (side: ConflictSide) => (side === 'ours' ? targetName : sourceName)

  if (draft) {
    return (
      <div className="flex flex-col gap-1.5 border-t border-line bg-brand-soft/30 px-2.5 py-2">
        <span className="text-10 text-ink-3">
          Different entities? Split into two nodes — keep one side at this id, add the other as a
          new node.
        </span>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-10 text-ink-3">Keep at this id:</span>
          {(['ours', 'theirs'] as const).map((side) => (
            <button
              key={side}
              type="button"
              disabled={!interactive}
              onClick={() => onDraftChange({ ...draft, keep: side })}
              aria-pressed={draft.keep === side}
              className={cn(
                'rounded-sm px-1.5 py-0.5 text-10 font-medium',
                draft.keep === side ? 'bg-brand-soft text-brand' : 'text-ink-3 hover:text-ink'
              )}
            >
              {keepName(side)}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-10 text-ink-3">
            New id for {keepName(draft.keep === 'ours' ? 'theirs' : 'ours')}:
          </span>
          <input
            autoFocus
            value={draft.newId}
            disabled={!interactive}
            onChange={(event) => onDraftChange({ ...draft, newId: event.target.value })}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                onConfirm()
              } else if (event.key === 'Escape') {
                event.preventDefault()
                onCancel()
              }
            }}
            aria-label="New node id"
            className="min-w-0 flex-1 rounded-sm border border-line bg-bg px-1 py-0.5 font-mono text-11 text-ink"
          />
          <button
            type="button"
            disabled={!interactive}
            onClick={onConfirm}
            className="rounded-sm bg-brand-soft px-1.5 py-0.5 text-10 font-medium text-brand hover:bg-brand-soft/70"
          >
            Split
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-sm px-1.5 py-0.5 text-10 text-ink-3 hover:text-ink"
          >
            Cancel
          </button>
        </div>
        {draft.error ? <span className="text-10 text-st-down">{draft.error}</span> : null}
      </div>
    )
  }

  if (current) {
    return (
      <div className="flex flex-wrap items-center gap-1.5 border-t border-line bg-brand-soft/30 px-2.5 py-1.5">
        <span className="text-10 font-medium tracking-wide text-brand uppercase">Split</span>
        <span className="text-11 text-ink">
          {keepName(current.keep)} keeps this id · new node{' '}
          <span className="font-mono text-brand" title={current.newId}>
            {current.newId}
          </span>
        </span>
        {interactive ? (
          <button
            type="button"
            onClick={onOpen}
            className="ml-auto rounded-sm px-1.5 py-0.5 text-10 text-brand hover:underline"
          >
            Edit
          </button>
        ) : null}
      </div>
    )
  }

  return (
    <div className="border-t border-line px-2.5 py-1.5">
      <button
        type="button"
        disabled={!interactive}
        onClick={onOpen}
        className={cn(
          'inline-flex items-center gap-1 rounded border border-brand/40 bg-brand-soft/50 px-2 py-1 text-11 font-semibold text-brand',
          interactive ? 'cursor-pointer hover:bg-brand-soft' : 'cursor-default opacity-60'
        )}
      >
        <Split className="size-3.5 shrink-0" />
        Different entities? Split into two nodes
      </button>
    </div>
  )
}
